import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    fetchMe,
    logout as apiLogout,
    getStoredUser,
    getStoredToken,
    getStoredLevel,
    clearStoredUser,
    getBookmarks,
    addBookmark as apiAddBookmark,
    updateBookmark as apiUpdateBookmark,
    deleteBookmark as apiDeleteBookmark,
} from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // Restore cached user immediately so UI renders without a flash of "logged out".
    const [user, setUser] = useState(() => getStoredUser());
    const [level, setLevel] = useState(() => getStoredLevel());
    const [bookmarks, setBookmarks] = useState([]);
    const [bookmarksLoaded, setBookmarksLoaded] = useState(false);
    // Track whether we have finished the server-side session check.
    const [sessionChecked, setSessionChecked] = useState(false);

    // On mount: if we have a stored token, verify it then load bookmarks.
    // - Success → refresh user object + load bookmarks independently.
    // - 401     → token expired/revoked; clear storage, force re-login.
    // - Network error → keep cached user display; don't kick the user out.
    useEffect(() => {
        const token = getStoredToken();
        if (!token) {
            setSessionChecked(true);
            return;
        }
        fetchMe()
            .then(u => {
                setUser(u);
                setLevel(u?.level ?? 0);
                setSessionChecked(true);
                // Load bookmarks in an independent chain so any bookmark error
                // does NOT affect user auth state.
                getBookmarks()
                    .then(list => { setBookmarks(list || []); setBookmarksLoaded(true); })
                    .catch(() => { /* bookmark load failure is non-fatal */ });
            })
            .catch(err => {
                setSessionChecked(true);
                // Only clear on an explicit 401 from fetchMe (token revoked/expired).
                if (err && (err.message?.includes('401') || err.message?.toLowerCase().includes('not authenticated'))) {
                    setUser(null);
                    clearStoredUser();
                }
                // Network errors / other failures → keep cached user display.
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshBookmarks = useCallback(async () => {
        try {
            const list = await getBookmarks();
            setBookmarks(list || []);
            setBookmarksLoaded(true);
        } catch (err) {
            // If we get a 401, retry once after a short delay (session may need a moment to settle).
            if (err?.message?.includes('401')) {
                setTimeout(async () => {
                    try {
                        const list = await getBookmarks();
                        setBookmarks(list || []);
                        setBookmarksLoaded(true);
                    } catch { /* give up silently */ }
                }, 500);
            }
        }
    }, []);

    /** Called after SSO login returns a user object — also loads bookmarks. */
    const loginSuccess = useCallback((userData) => {
        setUser(userData);
        setLevel(userData?.level ?? 0);
        // Small delay gives the server time to fully commit the session before
        // we make the first authenticated call (avoids a race-condition 401).
        setTimeout(() => {
            getBookmarks()
                .then(list => { setBookmarks(list || []); setBookmarksLoaded(true); })
                .catch(() => { /* ignore — user is still logged in */ });
        }, 300);
    }, []);

    const logout = useCallback(async () => {
        await apiLogout();
        setUser(null);
        setLevel(0);
        setBookmarks([]);
        setBookmarksLoaded(false);
    }, []);

    const addBookmark = useCallback(async (payload) => {
        const bm = await apiAddBookmark(payload);
        setBookmarks(prev => [bm, ...prev]);
        return bm;
    }, []);

    const updateBookmark = useCallback(async (id, payload) => {
        const bm = await apiUpdateBookmark(id, payload);
        setBookmarks(prev => prev.map(b => b.id === id ? bm : b));
        return bm;
    }, []);

    const deleteBookmark = useCallback(async (id) => {
        await apiDeleteBookmark(id);
        setBookmarks(prev => prev.filter(b => b.id !== id));
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            level,
            bookmarks,
            bookmarksLoaded,
            loginSuccess,
            logout,
            refreshBookmarks,
            addBookmark,
            updateBookmark,
            deleteBookmark,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
